import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Check, AlertCircle, Loader2, MapPin, X } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { toast } from 'sonner';
import { trackPurchase, trackCheckout, trackAddPaymentInfo } from '../utils/analytics';

const API = `${window.location.origin}/api`;

const PAYMENT_LABELS = {
  gcash: 'GCash',
  gotyme: 'GoTyme',
  paymaya: 'PayMaya',
  security_bank: 'Security Bank',
  cod: 'Cash on Delivery',
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { cart, cartTotal, clearCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const { formatPrice } = useCurrency();
  const fileInputRef = useRef(null);
  const orderPlacedRef = useRef(false);

  const [settings, setSettings] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [shippingRegion, setShippingRegion] = useState('');
  const [form, setForm] = useState({
    customer_name: '',
    phone: '',
    address_house: '',
    address_barangay: '',
    address_city: '',
    address_province: '',
    address_zip: '',
  });
  const [paymentMethod, setPaymentMethod] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [proofPath, setProofPath] = useState('');
  const [uploading, setUploading] = useState(false);
  // Distinct error state for the payment-proof upload so we can offer a
  // "Try again / choose another file" recovery UI instead of a frozen spinner
  // if the request stalls (e.g., slow Philippine mobile connections + service
  // worker interference on multipart POST). See handleUpload below.
  const [uploadError, setUploadError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    axios.get(`${API}/settings`).then(res => setSettings(res.data)).catch(console.error);
    // Fire begin_checkout once when the page mounts with cart contents
    if (cart && cart.length > 0) trackCheckout(cart, cartTotal);
    // Pre-fill from user profile if logged in
    if (isAuthenticated && user) {
      setForm(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        phone: user.phone || prev.phone,
      }));
      // Fetch saved addresses
      const token = localStorage.getItem('auth_token');
      if (token) {
        axios.get(`${API}/customer/addresses`, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => setSavedAddresses(res.data))
          .catch(() => {});
      }
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (cart.length === 0 && !orderPlacedRef.current) navigate('/cart');
  }, [cart, navigate]);

  const validatePhone = (phone) => {
    const cleaned = phone.replace(/\s|-/g, '');
    return /^(09\d{9}|\+639\d{9})$/.test(cleaned);
  };

  const formatPhone = (value) => {
    const digits = value.replace(/[^\d+]/g, '');
    return digits;
  };

  const validate = () => {
    const e = {};
    if (!form.customer_name.trim()) e.customer_name = 'Required';
    if (!form.phone.trim()) e.phone = 'Required';
    else if (!validatePhone(form.phone)) e.phone = 'Invalid PH number (09xx or +639xx)';
    if (!form.address_house.trim()) e.address_house = 'Required';
    if (!form.address_barangay.trim()) e.address_barangay = 'Required';
    if (!form.address_city.trim()) e.address_city = 'Required';
    if (!form.address_province.trim()) e.address_province = 'Required';
    if (!paymentMethod) e.payment = 'Select a payment method';
    if (paymentMethod && paymentMethod !== 'cod' && !proofPath) e.proof = 'Upload proof of payment';
    if (!shippingRegion) e.shipping = 'Select your shipping region';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleUpload = async (file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowed.includes(file.type)) {
      // Also clear any stale error from a previous failed attempt so the
      // dropzone doesn't display a misleading red error alongside the toast.
      setUploadError('Only JPG / PNG / WebP files allowed. Choose another file.');
      setProofFile(null);
      setProofPath('');
      toast.error('Only JPG/PNG/WebP files allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File too large. Max 5MB — choose a smaller image.');
      setProofFile(null);
      setProofPath('');
      toast.error('File too large. Max 5MB');
      return;
    }
    setUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      // Hard 30s timeout so a stalled multipart POST surfaces a real error
      // instead of leaving users staring at a frozen spinner. axios aborts
      // via AbortController under the hood.
      const res = await axios.post(`${API}/upload/payment-proof`, formData, {
        timeout: 30000,
        timeoutErrorMessage: 'Upload timed out — check your connection and try again.',
      });
      setProofPath(res.data.storage_path);
      setProofFile(file);
      setUploadError('');
      toast.success('Proof uploaded');
    } catch (err) {
      const isTimeout = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '');
      const msg = isTimeout
        ? 'Upload timed out. Check your internet and try again.'
        : (err?.response?.data?.detail || 'Upload failed. Try again or choose another file.');
      setUploadError(msg);
      setProofFile(null);
      setProofPath('');
      toast.error(msg);
    }
    setUploading(false);
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      // Grab Meta attribution context so the server can fire a matching
      // Purchase via Conversions API (dedups with the browser Pixel below).
      const readCookie = (name) => {
        try {
          const row = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
          return row ? decodeURIComponent(row.split('=').slice(1).join('=')) : undefined;
        } catch { return undefined; }
      };
      const orderData = {
        customer_name: form.customer_name.trim(),
        customer_email: isAuthenticated ? user?.email || '' : '',
        phone: form.phone.trim(),
        address_house: form.address_house.trim(),
        address_barangay: form.address_barangay.trim(),
        address_city: form.address_city.trim(),
        address_province: form.address_province.trim(),
        address_zip: form.address_zip.trim(),
        items: cart.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          price: item.price,
          variant: item.variant,
        })),
        payment_method: paymentMethod,
        proof_storage_path: proofPath,
        subtotal: cartTotal,
        shipping_fee: shippingFee,
        shipping_region: shippingRegion,
        total: orderTotal,
        // Meta CAPI attribution snapshot
        meta_fbp: readCookie('_fbp'),
        meta_fbc: readCookie('_fbc'),
        meta_user_agent: navigator.userAgent,
        meta_source_url: window.location.href,
      };
      const res = await axios.post(`${API}/orders`, orderData);
      trackPurchase(res.data.order_number, orderTotal, cart, shippingFee);
      orderPlacedRef.current = true;
      clearCart();
      navigate(`/order-confirmation/${res.data.order_number}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to place order');
    }
    setSubmitting(false);
  };

  const paymentMethods = settings?.payment_methods || {};
  const codEnabled = settings?.cod_enabled || false;
  const shippingFees = settings?.shipping_fees || {};
  const freeThreshold = settings?.free_shipping_threshold || 0;

  const shippingFee = (freeThreshold > 0 && cartTotal >= freeThreshold) ? 0 : (shippingRegion ? (shippingFees[shippingRegion] || 0) : 0);
  const orderTotal = cartTotal + shippingFee;

  const selectedPaymentInfo = paymentMethods[paymentMethod];
  const isCOD = paymentMethod === 'cod';
  const needsProof = paymentMethod && !isCOD;

  const REGIONS = [
    { key: 'metro_manila', label: 'Metro Manila (NCR)' },
    { key: 'luzon', label: 'Luzon' },
    { key: 'visayas', label: 'Visayas' },
    { key: 'mindanao', label: 'Mindanao' },
  ];

  return (
    <div data-testid="checkout-page" className="min-h-screen pb-28 sm:pb-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-3xl sm:text-4xl tracking-tight font-medium text-white mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Checkout
        </h1>

        {/* Order Summary */}
        <div className="bg-[#141414] border border-white/5 p-4 sm:p-6 mb-6" data-testid="order-summary">
          <h2 className="text-sm uppercase tracking-[0.15em] text-[#D4AF37] mb-4 font-sans font-medium">Order Summary</h2>
          {cart.map(item => (
            <div key={item.cartKey} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
              <div>
                <p className="text-white text-sm font-light">{item.product_name} <span className="text-white/40">x{item.quantity}</span></p>
                {item.variant && <p className="text-white/30 text-xs">{item.variant.size} &middot; {item.variant.type}</p>}
              </div>
              <p className="text-white/70 text-sm font-sans">{formatPrice(item.price * item.quantity)}</p>
            </div>
          ))}
          <div className="flex justify-between items-center pt-3 mt-2 border-t border-white/10">
            <span className="text-white/60 text-sm font-sans">Subtotal</span>
            <span className="text-white/70 text-sm font-sans">{formatPrice(cartTotal)}</span>
          </div>
          <div className="flex justify-between items-center pt-1">
            <span className="text-white/60 text-sm font-sans">Shipping</span>
            <span className="text-white/70 text-sm font-sans" data-testid="checkout-shipping-fee">
              {!shippingRegion ? 'Select region' : shippingFee === 0 ? 'Free' : formatPrice(shippingFee)}
            </span>
          </div>
          {freeThreshold > 0 && cartTotal < freeThreshold && (
            <p className="text-white/30 text-xs font-sans text-right">Free shipping on orders over {formatPrice(freeThreshold)}</p>
          )}
          <div className="flex justify-between items-center pt-2 border-t border-white/10 mt-1">
            <span className="text-white text-sm font-sans uppercase tracking-wider font-medium">Total</span>
            <span className="text-[#D4AF37] text-lg font-sans font-medium" data-testid="checkout-total">{formatPrice(orderTotal)}</span>
          </div>
        </div>

        {/* Customer Info */}
        <div className="space-y-4 mb-6" data-testid="customer-form">
          <h2 className="text-sm uppercase tracking-[0.15em] text-[#D4AF37] font-sans font-medium">Customer Information</h2>

          <div>
            <input
              type="text"
              placeholder="Full Name"
              value={form.customer_name}
              onChange={e => setForm({ ...form, customer_name: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 text-white py-3 px-0 text-base focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
              data-testid="input-name"
            />
            {errors.customer_name && <p className="text-red-400 text-xs mt-1">{errors.customer_name}</p>}
          </div>

          <div>
            <input
              type="tel"
              placeholder="Phone Number (09xx or +639xx)"
              value={form.phone}
              onChange={e => setForm({ ...form, phone: formatPhone(e.target.value) })}
              className="w-full bg-transparent border-b border-white/20 text-white py-3 px-0 text-base focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
              data-testid="input-phone"
            />
            {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
          </div>

          <h2 className="text-sm uppercase tracking-[0.15em] text-[#D4AF37] font-sans font-medium pt-2">Delivery Address</h2>

          {/* Saved Addresses */}
          {savedAddresses.length > 0 && (
            <div className="space-y-2" data-testid="saved-addresses">
              <p className="text-white/40 text-xs font-sans">Use a saved address:</p>
              <div className="flex flex-wrap gap-2">
                {savedAddresses.map(addr => (
                  <button
                    key={addr.id}
                    type="button"
                    onClick={() => setForm(prev => ({
                      ...prev,
                      address_house: addr.house,
                      address_barangay: addr.barangay,
                      address_city: addr.city,
                      address_province: addr.province,
                      address_zip: addr.zip || '',
                    }))}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-white/20 text-white/60 text-xs hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                    data-testid={`use-address-${addr.id}`}
                  >
                    <MapPin className="w-3 h-3" /> {addr.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <input
              type="text"
              placeholder="House No. / Street / Subdivision"
              value={form.address_house}
              onChange={e => setForm({ ...form, address_house: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 text-white py-3 px-0 text-base focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
              data-testid="input-address-house"
            />
            {errors.address_house && <p className="text-red-400 text-xs mt-1">{errors.address_house}</p>}
          </div>

          <div>
            <input
              type="text"
              placeholder="Barangay"
              value={form.address_barangay}
              onChange={e => setForm({ ...form, address_barangay: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 text-white py-3 px-0 text-base focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
              data-testid="input-address-barangay"
            />
            {errors.address_barangay && <p className="text-red-400 text-xs mt-1">{errors.address_barangay}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <input
                type="text"
                placeholder="City / Municipality"
                value={form.address_city}
                onChange={e => setForm({ ...form, address_city: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 text-white py-3 px-0 text-base focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
                data-testid="input-address-city"
              />
              {errors.address_city && <p className="text-red-400 text-xs mt-1">{errors.address_city}</p>}
            </div>
            <div>
              <input
                type="text"
                placeholder="ZIP Code (optional)"
                value={form.address_zip}
                onChange={e => setForm({ ...form, address_zip: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 text-white py-3 px-0 text-base focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
                data-testid="input-address-zip"
              />
            </div>
          </div>

          <div>
            <input
              type="text"
              placeholder="Province"
              value={form.address_province}
              onChange={e => setForm({ ...form, address_province: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 text-white py-3 px-0 text-base focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
              data-testid="input-address-province"
            />
            {errors.address_province && <p className="text-red-400 text-xs mt-1">{errors.address_province}</p>}
          </div>
        </div>

        {/* Shipping Region */}
        <div className="mb-6" data-testid="shipping-region-section">
          <h2 className="text-sm uppercase tracking-[0.15em] text-[#D4AF37] mb-3 font-sans font-medium">Shipping Region</h2>
          <div className="grid grid-cols-2 gap-2">
            {REGIONS.map(r => {
              const fee = shippingFees[r.key] || 0;
              const isFree = freeThreshold > 0 && cartTotal >= freeThreshold;
              return (
                <button
                  key={r.key}
                  onClick={() => setShippingRegion(r.key)}
                  data-testid={`shipping-region-${r.key}`}
                  className={`p-3 text-left transition-all border ${
                    shippingRegion === r.key
                      ? 'border-[#D4AF37] bg-[#D4AF37]/5'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  <p className={`text-sm font-sans ${shippingRegion === r.key ? 'text-white font-medium' : 'text-white/70'}`}>{r.label}</p>
                  <p className={`text-xs font-sans mt-0.5 ${shippingRegion === r.key ? 'text-[#D4AF37]' : 'text-white/40'}`}>
                    {isFree ? 'Free' : formatPrice(fee)}
                  </p>
                </button>
              );
            })}
          </div>
          {errors.shipping && <p className="text-red-400 text-xs mt-2">{errors.shipping}</p>}
        </div>

        {/* Payment Method */}
        <div className="mb-6" data-testid="payment-method-section">
          <h2 className="text-sm uppercase tracking-[0.15em] text-[#D4AF37] mb-4 font-sans font-medium">Payment Method</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.entries(paymentMethods).filter(([_, v]) => v.enabled).map(([key, method]) => (
              <button
                key={key}
                onClick={() => { setPaymentMethod(key); setProofPath(''); setProofFile(null); trackAddPaymentInfo(key, orderTotal); }}
                data-testid={`payment-method-${key}`}
                className={`payment-card p-3 text-center ${paymentMethod === key ? 'selected' : ''}`}
              >
                <p className="text-white text-sm font-sans font-medium">{method.name}</p>
                <p className="text-white/30 text-xs font-sans capitalize">{method.type === 'qr' ? 'QR Payment' : 'Bank Transfer'}</p>
              </button>
            ))}
            {codEnabled && (
              <button
                onClick={() => { setPaymentMethod('cod'); setProofPath(''); setProofFile(null); trackAddPaymentInfo('cod', orderTotal); }}
                data-testid="payment-method-cod"
                className={`payment-card p-3 text-center ${paymentMethod === 'cod' ? 'selected' : ''}`}
              >
                <p className="text-white text-sm font-sans font-medium">Cash on Delivery</p>
                <p className="text-white/30 text-xs font-sans">Pay upon receipt</p>
              </button>
            )}
          </div>
          {errors.payment && <p className="text-red-400 text-xs mt-2">{errors.payment}</p>}
        </div>

        {/* Payment Details & QR */}
        {selectedPaymentInfo && !isCOD && (
          <div className="bg-[#141414] border border-white/5 p-4 sm:p-6 mb-6" data-testid="payment-details">
            <h3 className="text-white text-base font-medium mb-4 font-sans">{selectedPaymentInfo.name} Payment Details</h3>
            {selectedPaymentInfo.qr_image && (
              <div className="flex justify-center mb-4">
                <img src={selectedPaymentInfo.qr_image} alt={`${selectedPaymentInfo.name} QR`} className="max-w-[260px] w-full h-auto border border-white/10" data-testid="payment-qr-image" />
              </div>
            )}
            {selectedPaymentInfo.account_name && (
              <p className="text-white/60 text-sm font-sans text-center">Account Name: <span className="text-white">{selectedPaymentInfo.account_name}</span></p>
            )}
            {selectedPaymentInfo.account_number && (
              <p className="text-white/60 text-sm font-sans text-center">Account Number: <span className="text-white">{selectedPaymentInfo.account_number}</span></p>
            )}
            {selectedPaymentInfo.bank_name && (
              <p className="text-white/60 text-sm font-sans text-center">Bank: <span className="text-white">{selectedPaymentInfo.bank_name}</span></p>
            )}
          </div>
        )}

        {isCOD && (
          <div className="bg-[#141414] border border-white/5 p-4 sm:p-6 mb-6" data-testid="cod-info">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white text-sm font-sans font-medium">Cash on Delivery</p>
                <p className="text-white/50 text-sm font-sans mt-1">Please prepare the exact amount of {formatPrice(orderTotal)} upon delivery.</p>
              </div>
            </div>
          </div>
        )}

        {/* Proof Upload */}
        {needsProof && (
          <div className="mb-6" data-testid="proof-upload-section">
            <h2 className="text-sm uppercase tracking-[0.15em] text-[#D4AF37] mb-3 font-sans font-medium">Proof of Payment</h2>
            <div
              className={`upload-dropzone p-6 text-center cursor-pointer ${proofFile ? 'border-[#D4AF37]' : ''}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
              onDragLeave={e => e.currentTarget.classList.remove('dragover')}
              onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('dragover'); handleUpload(e.dataTransfer.files[0]); }}
              data-testid="proof-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={e => handleUpload(e.target.files[0])}
                data-testid="proof-file-input"
              />
              {uploading ? (
                <div>
                  <Loader2 className="w-8 h-8 text-[#D4AF37] mx-auto animate-spin mb-2" />
                  <p className="text-white/40 text-xs font-sans" data-testid="proof-uploading-label">Uploading… up to 30 seconds</p>
                </div>
              ) : uploadError ? (
                <div data-testid="proof-upload-error">
                  <X className="w-8 h-8 text-red-400 mx-auto mb-2" />
                  <p className="text-red-400 text-sm font-sans mb-1">{uploadError}</p>
                  <p className="text-white/50 text-xs font-sans">Click to choose another file, or drag &amp; drop a new one</p>
                </div>
              ) : proofFile ? (
                <div>
                  <Check className="w-8 h-8 text-green-400 mx-auto mb-2" />
                  <p className="text-white/70 text-sm font-sans">{proofFile.name}</p>
                  <p className="text-white/30 text-xs mt-1 font-sans">Click to change</p>
                </div>
              ) : (
                <div>
                  <Upload className="w-8 h-8 text-white/30 mx-auto mb-2" />
                  <p className="text-white/50 text-sm font-sans">Upload screenshot of payment</p>
                  <p className="text-white/30 text-xs mt-1 font-sans">JPG, PNG or WebP &middot; Max 5MB</p>
                </div>
              )}
            </div>
            {errors.proof && <p className="text-red-400 text-xs mt-2">{errors.proof}</p>}
          </div>
        )}

        {/* Delivery Note */}
        <div className="bg-[#141414] border border-white/5 p-4 mb-6" data-testid="delivery-note">
          <p className="text-white/50 text-sm font-sans">
            Estimated delivery: <span className="text-white">3-7 business days</span> within the Philippines
          </p>
        </div>

        {/* Desktop Place Order */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="hidden sm:block w-full bg-[#D4AF37] text-black py-4 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="place-order-button"
        >
          {submitting ? 'Processing...' : 'Place Order'}
        </button>
      </div>

      {/* Mobile sticky Place Order */}
      <div className="sticky-bottom sm:hidden">
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full bg-[#D4AF37] text-black py-4 text-sm uppercase tracking-[0.15em] font-medium disabled:opacity-50"
          data-testid="mobile-place-order-button"
        >
          {submitting ? 'Processing...' : `Place Order · ${formatPrice(orderTotal)}`}
        </button>
      </div>
    </div>
  );
}
